/**
 * BrandBrief.jsx
 * What the Copilot knows about the business.
 *
 * This is not a new record. It is the brand itself — the same product summary,
 * audience, tone and banned claims that every caption in the app is already
 * written against. Editing it here edits the brand, because a second copy of
 * "who we are" is how two versions of the truth get started.
 *
 * Dependencies: services/api.js, components/Icons.jsx
 */

import { useEffect, useState } from 'react';
import { Check, Edit, Tag, Users, Chat, Layers, Eye, Warning } from '../Icons.jsx';
import { PLATFORMS } from '../dashboard/helpers.js';
import { api } from '../../services/api.js';

/** The fields the Copilot actually reads, in the order they matter to it. */
const FIELDS = [
  {
    key: 'product_summary',
    label: 'What it is',
    icon: Tag,
    placeholder: 'A dictation app that turns speech into tidy written notes.',
    rows: 3,
  },
  {
    key: 'audience',
    label: 'Audience',
    icon: Users,
    placeholder: 'Students, professionals and people who dictate frequently.',
    rows: 2,
  },
  { key: 'tone', label: 'Tone', icon: Chat, placeholder: 'Helpful, modern, confident.', rows: 2 },
  {
    key: 'default_cta',
    label: 'Preferred call to action',
    icon: Check,
    placeholder: 'Start a free trial.',
    rows: 1,
  },
  {
    key: 'banned_claims',
    label: 'Never say',
    icon: Warning,
    placeholder: 'Never claim it is 100% accurate. Never mention competitors by name.',
    rows: 2,
  },
];

export default function BrandBrief({ brand, platforms, onSaved, navigate }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(brand);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // A brand switch while the form is open must not save one brand's words onto
  // another.
  useEffect(() => {
    setDraft(brand);
    setEditing(false);
  }, [brand]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.brands.update(brand.id, {
        product_summary: draft.product_summary ?? '',
        audience: draft.audience ?? '',
        tone: draft.tone ?? '',
        default_cta: draft.default_cta ?? '',
        banned_claims: draft.banned_claims ?? '',
      });
      setEditing(false);
      await onSaved();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  }

  const missing = FIELDS.filter((f) => f.key !== 'banned_claims' && !brand[f.key]?.trim());

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Layers size={21} />
        </span>
        <div className="card__head-text">
          <h2>Brand brief</h2>
          <p>What Copilot knows about {brand.name}.</p>
        </div>
        <div className="card__head-end">
          {editing ? (
            <>
              <button className="btn btn--sm" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn--sm btn--primary" onClick={save} disabled={saving}>
                <Check size={15} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : (
            <button className="btn btn--sm" onClick={() => setEditing(true)}>
              <Edit size={15} /> Edit
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {/* Said once, here, rather than discovered when a strategy comes back
          vague: the model can only be as specific as the brief. */}
      {!editing && missing.length > 0 && (
        <div className="notice notice--info">
          <Warning />
          <span>
            Copilot writes better with more to go on. Still blank:{' '}
            {missing.map((f) => f.label.toLowerCase()).join(', ')}.
          </span>
        </div>
      )}

      <div className="brief">
        <div className="brief__row">
          <span className="brief__label">
            <Tag size={15} /> Brand
          </span>
          <span className="brief__value brief__value--strong">{brand.name}</span>
        </div>

        {FIELDS.map((field) => (
          <div className="brief__row" key={field.key}>
            <span className="brief__label">
              <field.icon size={15} /> {field.label}
            </span>
            {editing ? (
              <textarea
                className="brief__input"
                rows={field.rows}
                value={draft[field.key] ?? ''}
                placeholder={field.placeholder}
                onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
              />
            ) : (
              <span className={`brief__value${brand[field.key]?.trim() ? '' : ' brief__value--empty'}`}>
                {brand[field.key]?.trim() || 'Not set'}
              </span>
            )}
          </div>
        ))}

        {/* Not editable here: a platform is connected on the Brands screen by
            signing in to it, never by typing its name. */}
        <div className="brief__row">
          <span className="brief__label">
            <Eye size={15} /> Publishes to
          </span>
          <span className="brief__value">
            {platforms.length === 0 ? (
              <button className="link-more" onClick={() => navigate('/brands')}>
                No accounts connected — connect one
              </button>
            ) : (
              <span className="brief__platforms">
                {platforms.map((p) => {
                  const meta = PLATFORMS[p];
                  if (!meta) return null;
                  const { Icon: Mark, label, className } = meta;
                  return (
                    <span className={`platform-mark ${className}`} key={p} title={label}>
                      <Mark size={13} />
                      <span className="sr-only">{label}</span>
                    </span>
                  );
                })}
                <span className="brief__platform-names">
                  {platforms.map((p) => PLATFORMS[p]?.label).filter(Boolean).join(', ')}
                </span>
              </span>
            )}
          </span>
        </div>
      </div>
    </section>
  );
}
